import { apiClient, FlowSummary, L7AccessLogSummary, InventoryFlowsResponse, InventoryL7AccessLogsResponse } from '../../api/client';
import { UnifiedFlowData } from './types';

// --- VPC/VCN VALIDATION HELPERS ---

/**
 * Check if a VPC ID is valid (AWS VPC IDs start with 'vpc-')
 */
export function isValidVpcId(vpcId: string | undefined | null): boolean {
    return Boolean(vpcId && typeof vpcId === 'string' && vpcId.startsWith('vpc-'));
}

/**
 * Check if a VCN ID is valid (OCI VCN OCIDs start with 'ocid1.vcn.')
 */
export function isValidVcnId(vcnId: string | undefined | null): boolean {
    return Boolean(vcnId && typeof vcnId === 'string' && vcnId.startsWith('ocid1.vcn.'));
}

// --- LAYOUT & AGGREGATION HELPERS ---
export const PADDING = { container: 45, node: 15, namespace: 60 };
export const HEADER_HEIGHT = 30;

export const getLayoutConfig = (totalItems: number) => {
    const nodeSize = {
        width: 120,
        height: 80
    };

    // Calculate optimal grid dimensions for a near-square layout
    const optimalItemsPerRow = Math.ceil(Math.sqrt(totalItems));

    // Scale max columns based on item count to avoid extremely tall containers
    let maxCols = 6;
    if (totalItems > 100) {
        maxCols = 14;
    } else if (totalItems > 64) {
        maxCols = 10;
    }

    const itemsPerRow = Math.max(2, Math.min(optimalItemsPerRow, maxCols));

    return { itemsPerRow, nodeSize };
};

export const calculateChildNodePosition = (index: number, totalItems: number, nodeType?: string) => {
    const { itemsPerRow, nodeSize } = getLayoutConfig(totalItems);
    const col = index % itemsPerRow;
    const row = Math.floor(index / itemsPerRow);

    // Use larger spacing for namespace nodes to better accommodate flow arrows
    const padding = nodeType === 'namespace' ? PADDING.namespace : PADDING.node;
    const x = col * (nodeSize.width + padding) + PADDING.container;
    const y = row * (nodeSize.height + padding) + PADDING.container + HEADER_HEIGHT;
    return { x, y };
};

export const calculateContainerDimensions = (contentCount: number, nodeType?: string) => {
    if (contentCount === 0) return { width: 500, height: 250 };
    const { itemsPerRow, nodeSize } = getLayoutConfig(contentCount);
    const numRows = Math.ceil(contentCount / itemsPerRow);
    const numCols = Math.min(contentCount, itemsPerRow);

    // Use larger spacing for namespace containers
    const padding = nodeType === 'namespace' ? PADDING.namespace : PADDING.node;
    const calculatedWidth = numCols * (nodeSize.width + padding) - padding + PADDING.container * 2;
    const width = Math.max(300, calculatedWidth); // Enforce a minimum width
    const height = numRows * (nodeSize.height + padding) - padding + PADDING.container * 2 + HEADER_HEIGHT;
    return { width, height };
};

export const groupByApplication = (items: any[]): { appName: string; nodes: any[] }[] => {
    const grouped = items.reduce((acc, item) => {
        const appName = item.labels?.['app.kubernetes.io/name'] || item.labels?.['app'] || item.labels?.['k8s-app'];
        const key = appName || '---ungrouped---'; // Using a key that is unlikely to be a real app name
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(item);
        return acc;
    }, {} as Record<string, any[]>);

    // Always put ungrouped last
    const sortedEntries = Object.entries(grouped).sort(([a], [b]) => {
        if (a === '---ungrouped---') return 1;
        if (b === '---ungrouped---') return -1;
        return a.localeCompare(b);
    });

    return sortedEntries.map(([appName, nodes]) => ({ appName, nodes: nodes as any[] }));
};

// --- DATA FETCHING HELPERS ---

// Helper function to fetch cluster flows with proper limits for scalability
export const fetchClusterFlows = async (clusterUID: string, maxFlows: number = 5000, level?: string): Promise<FlowSummary[]> => {
    const allFlows: FlowSummary[] = [];
    let pageToken: string | undefined = undefined;
    let hasMore = true;
    const pageSize = 1000;

    while (hasMore && allFlows.length < maxFlows) {
        try {
            const response: InventoryFlowsResponse = await apiClient.getInventoryClusterFlows(clusterUID, pageSize, pageToken, level);
            const newFlows = response.flows || [];

            // Respect the maxFlows limit
            const remainingSpace = maxFlows - allFlows.length;
            const flowsToAdd = newFlows.slice(0, remainingSpace);
            allFlows.push(...flowsToAdd);

            hasMore = response.has_more && allFlows.length < maxFlows;
            pageToken = response.next_page_token;

        } catch (error) {
            console.error('Error fetching cluster flows:', error);
            break;
        }
    }

    if (allFlows.length >= maxFlows) {
        console.warn(`Flow limit reached: showing ${maxFlows} flows out of potentially more. Consider filtering for better performance.`);
    }

    return allFlows;
};

// Helper function to fetch namespace flows with proper limits for scalability
export const fetchNamespaceFlows = async (namespaceID: string, maxFlows: number = 2000): Promise<FlowSummary[]> => {
    const allFlows: FlowSummary[] = [];
    let pageToken: string | undefined = undefined;
    let hasMore = true;
    const pageSize = 200; // Smaller page size for namespace-level data

    while (hasMore && allFlows.length < maxFlows) {
        try {
            const response: InventoryFlowsResponse = await apiClient.getInventoryFlows(namespaceID, pageSize, pageToken);
            const newFlows = response.flows || [];

            // Respect the maxFlows limit
            const remainingSpace = maxFlows - allFlows.length;
            const flowsToAdd = newFlows.slice(0, remainingSpace);
            allFlows.push(...flowsToAdd);

            hasMore = response.has_more && allFlows.length < maxFlows;
            pageToken = response.next_page_token;

        } catch (error) {
            console.error('Error fetching namespace flows:', error);
            break;
        }
    }

    if (allFlows.length >= maxFlows) {
        console.warn(`Namespace flow limit reached: showing ${maxFlows} flows out of potentially more.`);
    }

    return allFlows;
};

// Helper function to fetch cluster L7 access logs with proper limits for scalability
export const fetchClusterL7AccessLogs = async (clusterUID: string, maxLogs: number = 3000, level?: string): Promise<L7AccessLogSummary[]> => {
    const allLogs: L7AccessLogSummary[] = [];
    let pageToken: string | undefined = undefined;
    let hasMore = true;
    const pageSize = 500; // Reasonable page size for L7 data

    while (hasMore && allLogs.length < maxLogs) {
        try {
            const response: InventoryL7AccessLogsResponse = await apiClient.getInventoryClusterL7AccessLogs(clusterUID, pageSize, pageToken, level);
            const newLogs = response.access_logs || [];

            // Respect the maxLogs limit
            const remainingSpace = maxLogs - allLogs.length;
            const logsToAdd = newLogs.slice(0, remainingSpace);
            allLogs.push(...logsToAdd);

            hasMore = response.has_more && allLogs.length < maxLogs;
            pageToken = response.next_page_token;

        } catch (error) {
            console.error('Error fetching cluster L7 access logs:', error);
            break;
        }
    }

    if (allLogs.length >= maxLogs) {
        console.warn(`L7 access log limit reached: showing ${maxLogs} logs out of potentially more. Consider filtering for better performance.`);
    }

    return allLogs;
};

// Helper function to fetch namespace L7 access logs with proper limits for scalability
export const fetchNamespaceL7AccessLogs = async (namespaceID: string, maxLogs: number = 1000): Promise<L7AccessLogSummary[]> => {
    const allLogs: L7AccessLogSummary[] = [];
    let pageToken: string | undefined = undefined;
    let hasMore = true;
    const pageSize = 200; // Smaller page size for namespace-level data

    while (hasMore && allLogs.length < maxLogs) {
        try {
            const response: InventoryL7AccessLogsResponse = await apiClient.getL7AccessLogs(namespaceID, pageSize, pageToken);
            const newLogs = response.access_logs || [];

            // Respect the maxLogs limit
            const remainingSpace = maxLogs - allLogs.length;
            const logsToAdd = newLogs.slice(0, remainingSpace);
            allLogs.push(...logsToAdd);

            hasMore = response.has_more && allLogs.length < maxLogs;
            pageToken = response.next_page_token;

        } catch (error) {
            console.error('Error fetching namespace L7 access logs:', error);
            break;
        }
    }

    if (allLogs.length >= maxLogs) {
        console.warn(`Namespace L7 access log limit reached: showing ${maxLogs} logs out of potentially more.`);
    }

    return allLogs;
};

// --- UTILITY FUNCTIONS FOR UNIFIED FLOW/L7 HANDLING ---

// Type guards
export const isL7AccessLog = (data: UnifiedFlowData): data is L7AccessLogSummary => {
    return 'l7_protocol' in data;
};

// Convert unified data to a display format for the flow details dialog
export const convertToDisplayFormat = (data: UnifiedFlowData[]): FlowSummary[] => {
    return data.map(item => {
        if (isL7AccessLog(item)) {
            // Convert L7 access log to FlowSummary format for display
            // Map L7 protocol to readable names
            let protocolName = item.protocol;
            if (item.l7_protocol === 'L7_PROTOCOL_HTTP') {
                protocolName = 'HTTP';
            } else if (item.l7_protocol === 'L7_PROTOCOL_TCP') {
                protocolName = 'TCP';
            }

            // For individual flow display, include method and path for HTTP
            const protocolDisplay = item.l7_protocol === 'L7_PROTOCOL_HTTP' && (item.method || item.path)
                ? `${protocolName}${item.method ? ` ${item.method}` : ''}${item.path ? ` ${item.path}` : ''}`
                : protocolName;

            // Clean up source name to remove redundant namespace suffix
            // e.g., "payment-service-7dd8c6fd9c-v6cv2.hr" -> "payment-service-7dd8c6fd9c-v6cv2"
            let cleanSrcName = item.src_name;
            if (item.src_kind === 'Pod' && cleanSrcName.endsWith(`.${item.src_namespace}`)) {
                cleanSrcName = cleanSrcName.slice(0, -(`.${item.src_namespace}`.length));
            }

            // Clean up destination name similarly if needed
            let cleanDstName = item.dst_name;
            if (item.dst_kind === 'Pod' && cleanDstName.endsWith(`.${item.dst_namespace}`)) {
                cleanDstName = cleanDstName.slice(0, -(`.${item.dst_namespace}`.length));
            }

            return {
                src_namespace: item.src_namespace,
                src_kind: item.src_kind,
                src_name: cleanSrcName,
                dst_namespace: item.dst_namespace,
                dst_kind: item.dst_kind,
                dst_name: cleanDstName,
                protocol: protocolDisplay,
                port: item.port,
                allowed: item.allowed,
                count: item.count,
                first_seen: item.first_seen,
                last_seen: item.last_seen,
                // Preserve aggregated protocols and ports arrays from backend
                protocols: item.protocols,
                ports: item.ports,
                // Preserve aggregated counts for mixed status
                allowed_count: item.allowed_count,
                denied_count: item.denied_count,
                suspicious_count: item.suspicious_count,
                suspicious: item.suspicious,
                // Store method and path separately for detailed display
                ...(item.l7_protocol === 'L7_PROTOCOL_HTTP' && {
                    method: item.method,
                    path: item.path,
                    response_code: item.response_code
                })
            } as FlowSummary & { method?: string; path?: string; response_code?: number };
        } else {
            return item as FlowSummary;
        }
    });
};

// Function to combine both flow types without aggregating individual logs
export const combineFlowData = (flows: FlowSummary[], l7Logs: L7AccessLogSummary[]): UnifiedFlowData[] => {
    // Simply combine both arrays without deduplication at the individual log level
    // Individual logs should remain separate for detailed viewing
    // Note: Server-side deduplication has already removed TCP/HTTP duplicates
    return [...flows, ...l7Logs];
};
